// src/lib/resolveMediaUrl.ts
import type { ACFMediaValue } from "@/types/acf";

const API_BASE = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");

/**
 * Combine an API-relative URL with the API base without duplicating the
 * subpath. If the incoming path already starts with the API's subpath
 * (e.g. "/es-api/wp-content/..."), prepend only the origin.
 */
function joinWithBase(path: string): string {
  if (!API_BASE) return path.startsWith("/") ? path : `/${path}`;

  const normalized = path.startsWith("/") ? path : `/${path}`;

  try {
    const originUrl = new URL(API_BASE);
    const originPath = originUrl.pathname.replace(/\/$/, "");
    if (originPath && normalized.startsWith(originPath + "/")) {
      return originUrl.origin + normalized;
    }
  } catch {
    // Fall through to naive concatenation if API_BASE isn't a valid URL
  }

  return `${API_BASE}${normalized}`;
}

export function resolveMediaUrl(value: ACFMediaValue | undefined): string | null {
  if (!value) return null;

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return null;

    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("//")) return `https:${raw}`;

    return joinWithBase(raw);
  }

  const url = typeof value.url === "string" ? value.url.trim() : "";
  if (!url) return null;

  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;

  return joinWithBase(url);
}